import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { retryEwayBill, cancelEwayBillClient, getEwayBillDetail } from "../generic/crud";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";
import { formatEwayDate, isExpired, groupEwbNo } from "./ewayBillUtils";

const CANCEL_REASONS = [
    { value: 1, label: "Duplicate" },
    { value: 2, label: "Order Cancelled" },
    { value: 3, label: "Data Entry Mistake" },
    { value: 4, label: "Others" },
];

/** Map an NIC/validation error to a concrete "do this next" hint for the operator. */
function nextSteps(error) {
    const msg = String(error || "").toLowerCase();
    const steps = [];
    if (msg.includes("gstin") || msg.includes("gst number")) {
        steps.push({ icon: "bi-person-vcard", text: "Open Customers and correct this customer's GST number (or clear it to send the bill as URP). The fix syncs to the server automatically." });
    }
    if (msg.includes("pin") || msg.includes("pincode")) {
        steps.push({ icon: "bi-geo-alt", text: "Check the pin code on the customer record — the portal validates it against the state." });
    }
    if (msg.includes("distance")) {
        steps.push({ icon: "bi-signpost", text: "Enter the correct transport distance below and regenerate." });
    }
    if (msg.includes("vehicle")) {
        steps.push({ icon: "bi-truck", text: "Correct the vehicle number below (e.g. KA01AB1234, no spaces) and regenerate." });
    }
    if (msg.includes("hsn")) {
        steps.push({ icon: "bi-upc-scan", text: "Fix the HSN code on the inventory/service item (must be 6+ digits). The invoice for this sale keeps its old HSN — ask your admin to regenerate it on the server." });
    }
    if (msg.includes("auth") || msg.includes("token") || msg.includes("credential") || msg.includes("password")) {
        steps.push({ icon: "bi-shield-lock", text: "The GST portal login needs attention — ask your admin to verify the credentials on the server's GST Services page." });
    }
    if (msg.includes("too soon") || msg.includes("busy") || msg.includes("reach")) {
        steps.push({ icon: "bi-hourglass-split", text: "The portal was busy — simply try Regenerate again in a minute." });
    }
    if (steps.length === 0) {
        steps.push({ icon: "bi-list-check", text: "Review the details below, correct anything that looks off, and regenerate." });
    }
    steps.push({ icon: "bi-arrow-repeat", text: "Then press Regenerate — the request goes through the RevCon server with your fixes applied." });
    return steps;
}

/**
 * Detail / recovery popup for a sale's e-Way bill, opened from the Sales List.
 * GENERATED: hero bill number + live portal details, View/Print and Cancel.
 * FAILED:    full portal error, guided next steps, regenerate with corrected data.
 * CANCELLED: struck-out bill + generate a fresh one.
 */
export function EwayBillDetailDialog({ show, sale, onHide, onUpdated }) {
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [billNo, setBillNo] = useState(null);
    const [invoiceNumber, setInvoiceNumber] = useState(null);
    const [error, setError] = useState(null);
    const [billDetail, setBillDetail] = useState(null); // full EwayBill doc from the server (dates etc.)
    // True when NIC issued this bill with an IRN: there is no separate e-Way bill
    // document, so printing means printing the e-Invoice sheet.
    const [fromEInvoice, setFromEInvoice] = useState(false);
    const [distance, setDistance] = useState("");
    const [vehicleNo, setVehicleNo] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [cancelMode, setCancelMode] = useState(false);
    const [cancelReason, setCancelReason] = useState(2);
    const [cancelRemark, setCancelRemark] = useState("Order Cancelled");

    useEffect(() => {
        if (show && sale) {
            const initial = String(sale.ewayBillStatus || "").toUpperCase();
            setStatus(initial);
            setBillNo(sale.ewayBillNo || null);
            setInvoiceNumber(sale.invoiceNumber || null);
            setError(sale.ewayBillError || null);
            setBillDetail(null);
            setFromEInvoice(false);
            setDistance(sale.leadKm ? String(Math.round(sale.leadKm)) : "");
            setVehicleNo(sale.vehicleNumber || "");
            setBusy(false);
            setCopied(false);
            setCancelMode(false);
            setCancelReason(2);
            setCancelRemark("Order Cancelled");
            if (initial === "GENERATED" || initial === "CANCELLED") {
                // Enrich with live portal details (generated on, valid until) — best effort.
                // The server is the source of truth for the bill's status: a bill cancelled
                // on the server must show as cancelled here even if the local copy is stale.
                getEwayBillDetail(sale.id)
                    .then(({ data }) => {
                        if (!data?.exists) return;
                        setBillDetail(data.ewayBill);
                        setFromEInvoice(!!data.fromEInvoice);
                        const live = String(data.ewayBill?.status || "").toUpperCase();
                        const liveStatus = live === "ACTIVE" ? "GENERATED" : live === "CANCELLED" ? "CANCELLED" : null;
                        if (liveStatus && liveStatus !== initial) {
                            setStatus(liveStatus);
                            if (data.ewayBill?.ewayBillNo) setBillNo(data.ewayBill.ewayBillNo);
                            // The backend mirrored the fresh status onto the local sale — reload the list.
                            if (onUpdated) onUpdated();
                        }
                    })
                    .catch(() => {});
            }
        }
    }, [show, sale]);

    if (!sale) return null;

    const generated = status === "GENERATED";
    const cancelled = status === "CANCELLED";
    const failed = status === "FAILED";
    const expired = generated && billDetail?.validUpto && isExpired(billDetail.validUpto);

    const copyBillNo = () => {
        try {
            navigator.clipboard.writeText(String(billNo || ""));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { /* clipboard unavailable */ }
    };

    const handleRetry = async () => {
        setBusy(true);
        try {
            const { data } = await retryEwayBill({
                saleId: sale.id,
                transDistance: distance.trim(),
                vehicleNo: vehicleNo.trim(),
            });
            if (data?.invoiceNumber) setInvoiceNumber(data.invoiceNumber);
            if (data?.success) {
                setStatus("GENERATED");
                setBillNo(data.ewayBillNo || null);
                setError(null);
                getEwayBillDetail(sale.id)
                    .then(({ data: d }) => {
                        if (!d?.exists) return;
                        setBillDetail(d.ewayBill);
                        setFromEInvoice(!!d.fromEInvoice);
                    })
                    .catch(() => {});
                showToast(`E-Way bill ${data.ewayBillNo || ""} generated`, TOAST_SUCCESS);
            } else {
                setStatus("FAILED");
                setError(data?.error || "Generation failed again");
                showToast(data?.error || "Generation failed again", TOAST_FAILURE);
            }
            if (onUpdated) onUpdated();
        } catch (e) {
            showToast(e?.response?.data?.error || "Could not reach the server", TOAST_FAILURE);
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = async () => {
        setBusy(true);
        try {
            const { data } = await cancelEwayBillClient({
                saleId: sale.id,
                cancelRsnCode: cancelReason,
                cancelRmrk: cancelRemark.trim(),
            });
            if (data?.success) {
                setStatus("CANCELLED");
                setCancelMode(false);
                showToast(`E-Way bill ${data.ewayBillNo || billNo || ""} cancelled`, TOAST_SUCCESS);
            } else {
                showToast(data?.error || "Cancellation failed", TOAST_FAILURE);
            }
            if (onUpdated) onUpdated();
        } catch (e) {
            showToast(e?.response?.data?.error || "Could not reach the server", TOAST_FAILURE);
        } finally {
            setBusy(false);
        }
    };

    const headTone = generated
        ? { bg: "bg-light-success", icon: "bi-patch-check-fill text-success", title: "E-Way Bill Generated" }
        : cancelled
            ? { bg: "bg-light", icon: "bi-slash-circle text-muted", title: "E-Way Bill Cancelled" }
            : { bg: "bg-light-danger", icon: "bi-x-octagon-fill text-danger", title: "E-Way Bill Failed" };

    const detail = (label, value, valueClass = "") => (
        <Col xs={6} md={4}>
            <div className="text-muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
            <div className={`fw-semibold small ${valueClass}`}>{value || "—"}</div>
        </Col>
    );

    return (
        <Modal show={show} onHide={() => !busy && onHide()} centered size="lg" scrollable>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="d-flex align-items-center">
                    <span className={`d-inline-flex align-items-center justify-content-center me-3 rounded ${headTone.bg}`}
                        style={{ width: 40, height: 40 }}>
                        <i className={`bi ${headTone.icon} fs-4`}></i>
                    </span>
                    <span>
                        <span className="d-block fs-5 fw-bold">{headTone.title}</span>
                        <span className="d-block fs-8 fw-normal text-muted">Ticket #{sale.ticketSequenceNumber || "-"} &middot; {sale.salesCustomer?.name || "-"}</span>
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                {/* Hero: the bill number itself */}
                {(generated || cancelled) && billNo && (
                    <div className="rounded p-4 mb-3 text-center"
                        style={generated
                            ? { background: "#e8fff3", border: "1px dashed #50cd89" }
                            : { background: "#f5f6f8", border: "1px dashed #c4c9d0" }}>
                        <div className="text-muted mb-1" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "1px" }}>E-Way Bill Number</div>
                        <div className={`fw-bold ${generated ? "" : "text-muted text-decoration-line-through"}`}
                            style={{ fontFamily: "monospace", fontSize: "1.7rem", letterSpacing: "2px", color: generated ? "#0f9d58" : undefined }}>
                            {groupEwbNo(billNo)}
                            <i className={`bi ${copied ? "bi-clipboard-check-fill text-success" : "bi-clipboard text-muted"} ms-3 fs-5`}
                                style={{ cursor: "pointer", verticalAlign: "middle" }}
                                title={copied ? "Copied!" : "Copy bill number"}
                                onClick={copyBillNo}></i>
                        </div>
                        <span className={`badge mt-1 ${cancelled ? "bg-light text-muted" : expired ? "bg-light-warning text-warning" : "bg-light-success text-success"}`}>
                            {cancelled ? "Cancelled" : expired ? "Validity Expired" : "Active"}
                        </span>
                    </div>
                )}

                {/* Details grid */}
                <div className="bg-light rounded p-3 mb-3">
                    <Row className="g-3">
                        {detail("Server Invoice", invoiceNumber)}
                        {detail("Vehicle", billDetail?.vehicleNo || sale.vehicleNumber)}
                        {detail("Distance", billDetail?.transDistance ? `${billDetail.transDistance} km` : (sale.leadKm ? `${Math.round(sale.leadKm)} km` : null))}
                        {(generated || cancelled) && detail("Generated On", formatEwayDate(billDetail?.ewayBillDate))}
                        {generated && detail("Valid Until", formatEwayDate(billDetail?.validUpto), expired ? "text-danger" : "")}
                        {cancelled && detail("Cancelled On", formatEwayDate(billDetail?.cancelDate))}
                    </Row>
                </div>

                {generated && !cancelMode && (
                    <div className="d-flex align-items-start text-muted" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-info-circle me-2 mt-1"></i>
                        <span>
                            The bill is live on the government portal — the driver can be stopped and checked against it.
                            Use <strong>View / Print</strong> for the official copy to hand over, or cancel it within
                            <strong> 24 hours</strong> of generation if something is wrong.
                        </span>
                    </div>
                )}

                {generated && cancelMode && (
                    <div className="border border-danger rounded p-3" style={{ borderStyle: "dashed" }}>
                        <div className="fw-bold small mb-2 text-danger"><i className="bi bi-slash-circle me-2"></i>Cancel this e-Way bill</div>
                        <Alert variant="warning" className="small py-2 text-gray-800">
                            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                            E-Way bills can only be cancelled within <strong className="text-gray-900">24 hours</strong> of
                            generation and not after an officer has verified them. This cannot be undone, and each
                            attempt is a paid portal call.
                        </Alert>
                        <Row className="g-2">
                            <Col sm={5}>
                                <Form.Label className="small text-muted mb-1">Reason</Form.Label>
                                <Form.Select size="sm" value={cancelReason} disabled={busy}
                                    onChange={(e) => setCancelReason(Number(e.target.value))}>
                                    {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </Form.Select>
                            </Col>
                            <Col sm={7}>
                                <Form.Label className="small text-muted mb-1">Remarks</Form.Label>
                                <Form.Control size="sm" maxLength={100} value={cancelRemark} disabled={busy}
                                    onChange={(e) => setCancelRemark(e.target.value)} />
                            </Col>
                        </Row>
                    </div>
                )}

                {cancelled && (
                    <Alert variant="secondary" className="small mb-3">
                        <i className="bi bi-slash-circle me-2"></i>
                        This e-Way bill was cancelled on the government portal and is no longer valid for transport.
                        Generate a fresh one below if the goods still need to move.
                    </Alert>
                )}

                {failed && (
                    <>
                        <Alert variant="danger" className="mb-3">
                            <div className="fw-bold small mb-1"><i className="bi bi-exclamation-octagon me-2"></i>What the government portal said</div>
                            <div className="small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {error || "No error details recorded."}
                            </div>
                        </Alert>
                        <div className="border rounded p-3 mb-3">
                            <div className="fw-bold small mb-2"><i className="bi bi-signpost-split me-2 text-primary"></i>How to fix it</div>
                            {nextSteps(error).map((s, i) => (
                                <div key={i} className="d-flex align-items-start small py-1">
                                    <i className={`bi ${s.icon} text-primary me-2 mt-1`}></i>
                                    <span className="text-muted">{s.text}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {(failed || cancelled) && (
                    <div className="border border-primary rounded p-3" style={{ borderStyle: "dashed" }}>
                        <div className="fw-bold small mb-2"><i className="bi bi-arrow-repeat me-2 text-primary"></i>{cancelled ? "Generate a fresh e-Way bill" : "Regenerate with corrected details"}</div>
                        <Row className="g-2">
                            <Col sm={5}>
                                <Form.Label className="small text-muted mb-1">Distance (km)</Form.Label>
                                <Form.Control size="sm" type="number" min="1" max="3999" value={distance}
                                    disabled={busy} onChange={(e) => setDistance(e.target.value)} />
                            </Col>
                            <Col sm={7}>
                                <Form.Label className="small text-muted mb-1">Vehicle Number</Form.Label>
                                <Form.Control size="sm" value={vehicleNo || "—"} readOnly plaintext
                                    className="fw-semibold text-gray-800 px-2"
                                    style={{ backgroundColor: "#f5f6f8", borderRadius: "6px" }} />
                                <div className="text-muted" style={{ fontSize: "0.68rem" }}>From the sale — not editable</div>
                            </Col>
                        </Row>
                        <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
                            Customer or item fixes made on this device sync to the server automatically —
                            make them first, then regenerate. Each attempt is a paid government portal call.
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                {generated && cancelMode ? (
                    <>
                        <Button variant="light" size="sm" onClick={() => setCancelMode(false)} disabled={busy}>Back</Button>
                        <Button variant="danger" size="sm" onClick={handleCancel} disabled={busy || !cancelRemark.trim()}>
                            {busy
                                ? <><Spinner size="sm" animation="border" className="me-2" />Cancelling…</>
                                : <><i className="bi bi-slash-circle me-2"></i>Confirm Cancel</>}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="light" size="sm" onClick={onHide} disabled={busy}>Close</Button>
                        {generated && (
                            <>
                                <Button variant="light-danger" size="sm" onClick={() => setCancelMode(true)} disabled={busy}>
                                    <i className="bi bi-slash-circle me-2"></i>Cancel E-Way Bill
                                </Button>
                                <Button variant="success" size="sm" onClick={() => { onHide(); navigate(fromEInvoice ? `/einvoice-print/${sale.id}` : `/eway-print/${sale.id}`); }}>
                                    <i className="bi bi-printer me-2"></i>View / Print
                                </Button>
                            </>
                        )}
                        {(failed || cancelled) && (
                            <Button variant="primary" size="sm" onClick={handleRetry} disabled={busy}>
                                {busy
                                    ? <><Spinner size="sm" animation="border" className="me-2" />Generating…</>
                                    : <><i className="bi bi-arrow-repeat me-2"></i>{cancelled ? "Generate New Bill" : "Regenerate E-Way Bill"}</>}
                            </Button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
